import { Hero as HeroSection } from 'components/Hero';
import Layout from 'components/Layout';
import { SEO } from 'components/SEO';
import { graphql } from 'gatsby';
import React, { lazy, useEffect } from 'react';
import track from 'utils/analytics/track';
import { ANALYTICS } from 'utils/constants/analytics';
import { IContentWithImage } from 'utils/types';

const MembersSatisfaction = lazy(
  () => import('containers/HealthPlansPage/MembersSatisfaction'),
);
const StatsSection = lazy(() => import('components/StatsBar'));
const HowItWorksSection = lazy(
  () => import('components/EverlyCarePage/HowItWorks'),
);

const StepCardListSection = lazy(() => import('components/StepCardList'));
const MemberComparisonSection = lazy(
  () => import('containers/HealthPlansPage/MemberComparisonSection'),
);
const MeasuresSection = lazy(
  () => import('containers/HealthPlansPage/MeasuresSection'),
);
const ImageSection = lazy(
  () => import('containers/HealthPlansPage/ImageSection'),
);

const components: Record<string, React.FC<any>> = {
  HeroSection,
  MembersSatisfaction,
  StatsSection,
  HowItWorksSection,
  StepCardList: StepCardListSection,
  MemberComparisonSection,
  MeasuresSection,
  ImageSection,
};

type Props = {
  data: {
    healthPlanContent: {
      title: string;
      hero: {
        eyebrow: string;
        headline: string;
        subHeadline: string;
        description: {
          childMarkdownRemark: {
            rawMarkdownBody: string;
          };
        };
        backgroundColor: string;
        desktopImage: {
          url: string;
          title: string;
        };
        cta: {
          text: string;
          url: string;
        };
      };
      sections: {
        componentName: string;
        headline: string;
        subHeadline?: string;
        sectionImage?: {
          title: string;
          gatsbyImageData: any;
        };
        description: {
          description: string;
          childMarkdownRemark: {
            rawMarkdownBody: string;
          };
        };
        slug: string;
        title?: string;
        listOfContentWithImages?: IContentWithImage[];
        container?: Array<{
          statsCollection?: {
            prefix: string;
            suffix: string;
            value: string;
            label: string;
          }[];
          logo?: { file: { url: string } };
          bannerHeading?: string;
          mobileBackgroundImage?: { file: { url: string } };
          text?: string;
          textColor?: string;
          ctaHeading?: string;
          backgroundImage?: { file: { url: string } };
          cta?: { url: string; label: string };
        }>;
        backgroundColor?: string;
        disclaimer?: {
          childMarkdownRemark: {
            html: string;
            rawMarkdownBody: string;
          };
        };
      }[];
    };
  };
};

export const Head = () => <SEO />;

export const healthPlanPage = (props: Props) => {
  // @ts-ignore
  const { hero, sections } = props.data.contentfulPage001;

  useEffect(() => {
    track({
      event: ANALYTICS.EVENTS.VIEWED_EHS_PAGE,
      data: {
        label: 'EHS Health Plans page',
      },
    });
  }, []);

  return (
    <Layout>
      <HeroSection {...hero} />
      {sections.map(
        (
          section: Props['data']['healthPlanContent']['sections'][number],
          i: number,
        ) => {
          const Component =
            components[section.componentName as keyof typeof components];

          if (!Component) return null;

          if (
            section.componentName === 'StatsSection' &&
            section.container?.[0]?.statsCollection
          ) {
            return (
              <StatsSection
                stats={section.container[0].statsCollection}
                disclaimer={section.disclaimer}
                backgroundColor={section.backgroundColor || ''}
                textColor={section.container[0].textColor || ''}
              />
            );
          }

          if (section.componentName === 'StepCardList') {
            return (
              <StepCardListSection
                key={`${section.componentName}-${i}`}
                {...section}
                listOfContentWithImages={section.listOfContentWithImages ?? []}
                eyebrow={section.title || ''}
                heading={section.headline}
                description={section.description}
              />
            );
          }

          return (
            // @ts-ignore
            <Component key={`${section.componentName}-${i}`} {...section} />
          );
        },
      )}
    </Layout>
  );
};

export default healthPlanPage;

export const data = graphql`
  query healthPlanContent {
    contentfulPage001(displayName: { eq: "Health Plans Page | EHS" }) {
      hero {
        ... on ContentfulHeroSection {
          eyebrow
          headline
          description {
            childMarkdownRemark {
              rawMarkdownBody
            }
          }
          backgroundColor
          desktopImage {
            url
            title
          }
          cta {
            text
            url
          }
        }
      }
      sections {
        headline
        title
        subHeadline
        componentName
        slug
        backgroundColor
        description {
          description
        }
        disclaimer {
          childMarkdownRemark {
            html
            rawMarkdownBody
          }
        }
        sectionImage {
          title
          url
          gatsbyImageData(placeholder: BLURRED, quality: 75)
        }
        cta {
          text
          url
        }
        listOfContentWithImages {
          ... on ContentfulContentWithImage {
            heading
            description {
              childMarkdownRemark {
                rawMarkdownBody
                html
              }
            }
            image {
              url
            }
          }
        }
        container {
          ... on ContentfulStatsBar {
            textColor
            statsCollection {
              prefix
              suffix
              value
              label
            }
          }
          ... on ContentfulComparisonTable {
            memberA {
              title
              subtitle
              image {
                description
                gatsbyImageData(layout: CONSTRAINED, width: 400, quality: 80)
              }
            }
            memberB {
              title
              subtitle
              image {
                description
                gatsbyImageData(layout: CONSTRAINED, width: 400, quality: 80)
              }
            }
            rows {
              id
              label
              memberAContent {
                memberAContent
              }
              memberBContent {
                memberBContent
              }
              colorMemberA
              colorMemberB
            }
          }
          ... on ContentfulHealthTest {
            image {
              gatsbyImageData(layout: CONSTRAINED, width: 400, quality: 80)
            }
            measures
            title
          }
        }
      }
    }
  }
`;
